/**
 * /market/:slug/evento/:eventSlug — Detalle público de evento + compra de entrada.
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { ArrowLeft, CalendarDays, MapPin, Users, ExternalLink, CheckCircle2 } from "lucide-react";
import { marketSurfaceStyle } from "@/lib/market-theme";
import { StructuraPlatformSignature } from "@/components/StructuraPlatformSignature";
import { loadMarketCatalog, type MarketCatalogResult } from "@/lib/market-catalog";
import { renderMarketPublicOffline } from "@/lib/market-public-offline-gate";
import { dbSmGetEvent, type SmEvent } from "@/lib/db-service/events";
import { getRequest } from "@tanstack/react-start/server";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { toast } from "sonner";
import { MarketPageSkeleton } from "@/components/market/MarketSkeletons";

type EventoDetailData = {
  catalog: MarketCatalogResult;
  event: SmEvent | null;
};

const fetchEventoDetail = createServerFn({ method: "GET" })
  .inputValidator((input: { slug: string; eventSlug: string }) => input)
  // @ts-expect-error ServerFnCtx framework type mismatch
  .handler(async ({ data }): Promise<EventoDetailData> => {
    const request = getRequest();
    const catalog = await loadMarketCatalog(data.slug, { request });
    let event: SmEvent | null = null;
    if (catalog.ok) {
      event = await dbSmGetEvent(catalog.data.tenant.id, data.eventSlug);
    }
    return { catalog, event };
  });

export const Route = createFileRoute("/market_/$slug/evento/$eventSlug")({
  pendingComponent: MarketPageSkeleton,
  pendingMs: 0,
  loader: async ({ params }) =>
    fetchEventoDetail({ data: { slug: params.slug, eventSlug: params.eventSlug } }),
  component: EventoDetailPage,
  head: ({ params }) => ({
    meta: [{ title: `Evento — ${params.slug}` }],
    links: [
      { rel: "manifest", href: `/api/manifest/${params.slug}` },
      {
        rel: "stylesheet",
        href: "https://fonts.googleapis.com/css2?family=Hanken+Grotesk:wght@400;600;700;800&family=Inter:wght@400;500;600&display=swap",
      },
    ],
  }),
});

function formatEventDate(startsAt: Date): string {
  return format(startsAt, "EEEE d 'de' MMMM yyyy 'a las' HH:mm", { locale: es });
}

function EventoDetailPage() {
  const { slug, eventSlug } = Route.useParams();
  const { catalog, event } = Route.useLoaderData() as EventoDetailData;

  const [form, setForm] = useState({ name: "", email: "", phone: "", quantity: 1 });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const offlineScreen = renderMarketPublicOffline(catalog);
  if (offlineScreen) return offlineScreen;

  if (!catalog.ok || !catalog.data) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 p-6 text-center">
        <h1 className="text-xl font-semibold">Tienda no disponible</h1>
        <p className="text-muted-foreground">{!catalog.ok ? catalog.error : ""}</p>
      </div>
    );
  }

  const { tenant, config } = catalog.data;
  const brandStyle = marketSurfaceStyle({
    primaryColor: config.primaryColor,
    secondaryColor: config.secondaryColor,
    backgroundColor: config.backgroundColor,
  });

  if (!event) {
    return (
      <div className="market-surface min-h-screen" style={brandStyle}>
        <div className="flex min-h-screen flex-col items-center justify-center gap-3 p-6 text-center">
          <span className="text-5xl opacity-30">🎟️</span>
          <h1 className="market-font-display text-xl font-bold text-[var(--market-on-surface)]">
            Evento no encontrado
          </h1>
          <Link
            to="/market/$slug/eventos"
            params={{ slug }}
            className="rounded-full bg-[var(--market-accent)] px-5 py-2.5 text-sm font-semibold text-[var(--primary-foreground)]"
          >
            Ver todos los eventos
          </Link>
        </div>
      </div>
    );
  }

  const cuposDisponibles = event.capacity - event.soldCount;
  const soldOut = cuposDisponibles <= 0 || event.status === "sold_out" || event.status === "closed" || event.status === "cancelled";
  const maxQty = Math.min(cuposDisponibles, 10);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Nombre y email son requeridos");
      return;
    }
    setSubmitting(true);
    try {
      const resp = await fetch("/api/eventos/tickets", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          eventId: event.id,
          tenantId: catalog.ok ? catalog.data.tenant.id : "",
          buyerName: form.name.trim(),
          buyerEmail: form.email.trim(),
          buyerPhone: form.phone.trim() || null,
          quantity: form.quantity,
        }),
      });
      const data = await resp.json() as { ok?: boolean; error?: string };
      if (!resp.ok) throw new Error((data as { error?: string }).error ?? "Error al crear la reserva");
      setSuccess(true);
      toast.success("Reserva creada exitosamente");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Error al procesar la reserva");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="market-surface min-h-screen pb-16" style={brandStyle}>
      {/* Header */}
      <header className="sticky top-0 z-40 flex h-14 items-center gap-3 border-b border-[var(--market-surface-variant)] bg-[var(--market-surface)] px-4 md:h-16 md:px-12">
        <Link
          to="/market/$slug/eventos"
          params={{ slug }}
          className="flex h-9 w-9 items-center justify-center rounded-full text-[var(--market-on-surface-variant)] hover:bg-[var(--market-surface-bright)] transition-colors"
          aria-label="Volver a eventos"
        >
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <div className="flex min-w-0 flex-1 items-center gap-2.5">
          {tenant.logoUrl && (
            <img
              src={tenant.logoUrl}
              alt=""
              className="h-7 w-7 shrink-0 rounded-lg object-contain bg-[var(--market-surface-bright)] p-0.5 ring-1 ring-[var(--market-surface-variant)]"
            />
          )}
          <span className="market-font-display truncate text-base font-bold text-[var(--market-accent)]">
            {tenant.name}
          </span>
        </div>
      </header>

      <div className="mx-auto max-w-3xl px-4 py-8 md:px-12">
        <div className="grid gap-8 md:grid-cols-[1fr_360px]">
          {/* Info del evento */}
          <div>
            {/* Imagen de portada */}
            {event.coverImageUrl ? (
              <div className="mb-6 aspect-video overflow-hidden rounded-2xl">
                <img
                  src={event.coverImageUrl}
                  alt={event.title}
                  className="h-full w-full object-cover"
                />
              </div>
            ) : (
              <div className="mb-6 flex aspect-video items-center justify-center rounded-2xl bg-[var(--market-surface-container)] text-6xl opacity-20">
                🎟️
              </div>
            )}

            <h1 className="market-font-display text-2xl font-extrabold leading-tight text-[var(--market-on-surface)] md:text-3xl">
              {event.title}
            </h1>

            {/* Precio */}
            <div className="mt-3">
              <span className="market-font-display text-3xl font-bold text-[var(--market-accent)]">
                {event.price > 0
                  ? new Intl.NumberFormat("es-AR", {
                      style: "currency",
                      currency: "ARS",
                      maximumFractionDigits: 0,
                    }).format(event.price)
                  : "Gratuito"}
              </span>
              {event.price > 0 && (
                <span className="ml-2 text-sm text-[var(--market-on-surface-variant)]">/ entrada</span>
              )}
            </div>

            {/* Disponibilidad */}
            <div className="mt-3">
              {soldOut ? (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-red-500/10 px-3 py-1 text-sm font-medium text-red-500">
                  Sin cupos disponibles
                </span>
              ) : cuposDisponibles <= 10 ? (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-orange-500/10 px-3 py-1 text-sm font-medium text-orange-500">
                  Últimos {cuposDisponibles} cupos
                </span>
              ) : (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-green-500/10 px-3 py-1 text-sm font-medium text-green-600">
                  {cuposDisponibles} cupos disponibles
                </span>
              )}
            </div>

            {/* Detalles */}
            <div className="mt-5 space-y-3 text-sm text-[var(--market-on-surface-variant)]">
              <div className="flex items-start gap-3">
                <CalendarDays className="mt-0.5 h-5 w-5 shrink-0 text-[var(--market-accent)]" />
                <div>
                  <p className="font-medium capitalize text-[var(--market-on-surface)]">
                    {formatEventDate(event.startsAt)}
                  </p>
                  {event.endsAt && (
                    <p className="text-xs">
                      Hasta las {format(event.endsAt, "HH:mm", { locale: es })}
                    </p>
                  )}
                </div>
              </div>

              <div className="flex items-start gap-3">
                <MapPin className="mt-0.5 h-5 w-5 shrink-0 text-[var(--market-accent)]" />
                <div>
                  {event.locationType === "online" ? (
                    <>
                      <p className="font-medium text-[var(--market-on-surface)]">Evento online</p>
                      {event.onlineLink && (
                        <a
                          href={event.onlineLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 text-xs text-[var(--market-accent)] hover:underline"
                        >
                          Ver link <ExternalLink className="h-3 w-3" />
                        </a>
                      )}
                    </>
                  ) : (
                    <>
                      <p className="font-medium text-[var(--market-on-surface)]">
                        {event.locationAddress ?? "Presencial"}
                      </p>
                      {event.locationNotes && <p className="text-xs">{event.locationNotes}</p>}
                      {event.locationType === "hibrido" && event.onlineLink && (
                        <a
                          href={event.onlineLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 text-xs text-[var(--market-accent)] hover:underline"
                        >
                          Link online <ExternalLink className="h-3 w-3" />
                        </a>
                      )}
                    </>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-3">
                <Users className="h-5 w-5 shrink-0 text-[var(--market-accent)]" />
                <span>
                  {event.soldCount} / {event.capacity} entradas vendidas
                </span>
              </div>
            </div>

            {/* Descripción */}
            {event.description && (
              <div className="mt-6 border-t border-[var(--market-surface-variant)] pt-5">
                <h2 className="market-font-display mb-2 text-lg font-bold text-[var(--market-on-surface)]">
                  Descripción
                </h2>
                <div className="whitespace-pre-line text-sm leading-relaxed text-[var(--market-on-surface-variant)]">
                  {event.description}
                </div>
              </div>
            )}

            {/* Política de reembolso */}
            {event.refundEnabled && event.refundPolicy && (
              <div className="mt-4 rounded-xl border border-[var(--market-surface-variant)] bg-[var(--market-surface-container)] p-4">
                <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-[var(--market-on-surface-variant)]">
                  Política de reembolso
                </p>
                <p className="text-sm text-[var(--market-on-surface-variant)]">{event.refundPolicy}</p>
              </div>
            )}
          </div>

          {/* Formulario de compra */}
          <div className="md:sticky md:top-20 md:self-start">
            <div className="rounded-2xl border border-[var(--market-surface-variant)] bg-[var(--market-surface)] p-5 shadow-sm">
              {success ? (
                <div className="flex flex-col items-center gap-3 py-6 text-center">
                  <CheckCircle2 className="h-14 w-14 text-emerald-500" />
                  <h2 className="market-font-display text-lg font-bold text-[var(--market-on-surface)]">
                    ¡Reserva registrada!
                  </h2>
                  <p className="text-sm text-[var(--market-on-surface-variant)]">
                    Tu reserva fue registrada exitosamente. Pronto recibirás más información sobre el evento.
                  </p>
                  <Link
                    to="/market/$slug/eventos"
                    params={{ slug }}
                    className="mt-2 rounded-full bg-[var(--market-accent)] px-5 py-2 text-sm font-bold text-[var(--primary-foreground)]"
                  >
                    Ver más eventos
                  </Link>
                </div>
              ) : soldOut ? (
                <div className="flex flex-col items-center gap-3 py-6 text-center">
                  <span className="text-4xl">😔</span>
                  <h2 className="market-font-display text-lg font-bold text-[var(--market-on-surface)]">
                    Sin cupos disponibles
                  </h2>
                  <p className="text-sm text-[var(--market-on-surface-variant)]">
                    Este evento ya no tiene entradas disponibles.
                  </p>
                </div>
              ) : (
                <>
                  <h2 className="market-font-display mb-4 text-lg font-bold text-[var(--market-on-surface)]">
                    Comprar entrada
                  </h2>
                  <form onSubmit={(e) => void handleSubmit(e)} className="space-y-3">
                    <div>
                      <label className="mb-1 block text-xs font-medium text-[var(--market-on-surface-variant)]">
                        Nombre completo *
                      </label>
                      <input
                        required
                        className="w-full rounded-lg border border-[var(--market-surface-variant)] bg-[var(--market-surface-container)] px-3 py-2 text-sm text-[var(--market-on-surface)] placeholder-[var(--market-on-surface-variant)] focus:outline-none focus:ring-2 focus:ring-[var(--market-accent)]"
                        placeholder="Tu nombre"
                        value={form.name}
                        onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-medium text-[var(--market-on-surface-variant)]">
                        Email *
                      </label>
                      <input
                        required
                        type="email"
                        className="w-full rounded-lg border border-[var(--market-surface-variant)] bg-[var(--market-surface-container)] px-3 py-2 text-sm text-[var(--market-on-surface)] placeholder-[var(--market-on-surface-variant)] focus:outline-none focus:ring-2 focus:ring-[var(--market-accent)]"
                        placeholder="tu@email.com"
                        value={form.email}
                        onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-medium text-[var(--market-on-surface-variant)]">
                        Teléfono (opcional)
                      </label>
                      <input
                        type="tel"
                        className="w-full rounded-lg border border-[var(--market-surface-variant)] bg-[var(--market-surface-container)] px-3 py-2 text-sm text-[var(--market-on-surface)] placeholder-[var(--market-on-surface-variant)] focus:outline-none focus:ring-2 focus:ring-[var(--market-accent)]"
                        placeholder="+54 11 ..."
                        value={form.phone}
                        onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-medium text-[var(--market-on-surface-variant)]">
                        Cantidad de entradas
                      </label>
                      <select
                        className="w-full rounded-lg border border-[var(--market-surface-variant)] bg-[var(--market-surface-container)] px-3 py-2 text-sm text-[var(--market-on-surface)] focus:outline-none focus:ring-2 focus:ring-[var(--market-accent)]"
                        value={form.quantity}
                        onChange={(e) => setForm((f) => ({ ...f, quantity: Number(e.target.value) }))}
                      >
                        {Array.from({ length: maxQty }, (_, i) => i + 1).map((n) => (
                          <option key={n} value={n}>
                            {n} {n === 1 ? "entrada" : "entradas"}
                          </option>
                        ))}
                      </select>
                    </div>

                    {event.price > 0 && (
                      <div className="rounded-lg bg-[var(--market-surface-container)] p-3">
                        <div className="flex justify-between text-sm">
                          <span className="text-[var(--market-on-surface-variant)]">
                            {form.quantity} x {new Intl.NumberFormat("es-AR", { style: "currency", currency: "ARS", maximumFractionDigits: 0 }).format(event.price)}
                          </span>
                          <span className="font-bold text-[var(--market-on-surface)]">
                            {new Intl.NumberFormat("es-AR", { style: "currency", currency: "ARS", maximumFractionDigits: 0 }).format(event.price * form.quantity)}
                          </span>
                        </div>
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={submitting}
                      className="flex w-full items-center justify-center gap-2 rounded-full bg-[var(--market-accent)] py-3 text-sm font-bold text-[var(--primary-foreground)] transition-opacity hover:opacity-90 active:scale-95 disabled:opacity-50"
                    >
                      {submitting ? (
                        <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                      ) : null}
                      {submitting ? "Procesando..." : "Reservar entrada"}
                    </button>

                    <p className="text-center text-xs text-[var(--market-on-surface-variant)]">
                      Recibirás la confirmación por email
                    </p>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="mt-8 border-t border-[var(--market-surface-variant)]">
        <StructuraPlatformSignature />
      </div>
    </div>
  );
}
